import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import SuperAdminLayout from '../components/SuperAdminLayout';

const SuperAdminTenants = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [editingTenant, setEditingTenant] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [newTenant, setNewTenant] = useState({
    name: '',
    subdomain: '',
    contact: { email: '', phone: '' },
    password: '',
    subscription: { plan: 'basic' },
    allowedFeatures: ['entry_ticket', 'parking_ticket', 'other']
  });


  useEffect(() => {
    fetchTenants();
  }, []);

  const filteredTenants = tenants.filter(tenant => {
    const matchesSearch = tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tenant.contact?.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && tenant.subscription?.isActive) ||
                         (filterStatus === 'inactive' && !tenant.subscription?.isActive);
    return matchesSearch && matchesStatus;
  });

  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/tenants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenants(response.data.tenants || []);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      setTenants([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTenant = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingTenant) {
        const updateData = {
          name: newTenant.name,
          subdomain: newTenant.subdomain,
          contact: newTenant.contact,
          subscription: newTenant.subscription,
          allowedFeatures: newTenant.allowedFeatures
        };
        
        // Only include password if it's provided
        if (newTenant.password && newTenant.password.trim()) {
          updateData.adminPassword = newTenant.password;
        }
        
        const response = await axios.put(`${API_BASE_URL}/api/tenants/${editingTenant._id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const updatedTenants = tenants.map(t => 
          t._id === editingTenant._id ? response.data.tenant : t
        );
        setTenants(updatedTenants);
        alert('✨ Tenant updated successfully!');
      } else {
        const response = await axios.post(`${API_BASE_URL}/api/tenants`, {
          name: newTenant.name,
          subdomain: newTenant.subdomain,
          email: newTenant.contact.email,
          phone: newTenant.contact.phone,
          adminName: newTenant.name + ' Admin',
          adminEmail: newTenant.contact.email,
          adminPassword: newTenant.password,
          allowedFeatures: newTenant.allowedFeatures
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const createdTenant = {
          ...response.data.tenant,
          subscription: { ...newTenant.subscription, isActive: true },
          stats: { totalBookings: 0, revenue: 0, activeTickets: 0 }
        };
        setTenants([...tenants, createdTenant]);
        alert('✨ Tenant created successfully!');
      }
      
      setShowAddModal(false);
      setEditingTenant(null);
      setNewTenant({ 
        name: '', 
        subdomain: '', 
        contact: { email: '', phone: '' },
        password: '',
        subscription: { plan: 'basic' },
        allowedFeatures: ['entry_ticket', 'parking_ticket', 'other']
      });
    } catch (error) {
      alert('Error saving tenant: ' + (error.response?.data?.message || 'Backend API needed.'));
    }
  };

  const startEditTenant = (tenant) => {
    console.log('Editing tenant:', tenant);
    console.log('Tenant allowedFeatures:', tenant.allowedFeatures);
    setEditingTenant(tenant);
    setNewTenant({
      name: tenant.name,
      subdomain: tenant.subdomain,
      contact: { ...tenant.contact },
      password: '', // Don't pre-fill password for security
      subscription: { ...tenant.subscription },
      allowedFeatures: tenant.allowedFeatures || ['entry_ticket', 'parking_ticket', 'other']
    });
    setShowAddModal(true);
  };

  const handleDeleteTenant = async (tenantId) => {
    if (window.confirm('⚠️ Are you sure you want to delete this tenant? This action cannot be undone.')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API_BASE_URL}/api/tenants/${tenantId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTenants(tenants.filter(t => t._id !== tenantId));
        alert('🗑️ Tenant deleted successfully!');
      } catch (error) {
        alert('Error deleting tenant: ' + (error.response?.data?.message || 'Backend API needed for actual deletion.'));
      }
    }
  };

  const toggleTenantStatus = async (tenantId, currentStatus) => {
    try {
      // Mock update for now
      setTenants(tenants.map(tenant => 
        tenant._id === tenantId 
          ? { ...tenant, subscription: { ...tenant.subscription, isActive: !currentStatus } }
          : tenant
      ));
      
      // Update selected tenant if it's the one being modified
      if (selectedTenant && selectedTenant._id === tenantId) {
        setSelectedTenant({
          ...selectedTenant,
          subscription: { ...selectedTenant.subscription, isActive: !currentStatus }
        });
      }
      
      alert(`Tenant ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
    } catch (error) {
      alert('Error updating tenant status');
    }
  };

  return (
    <SuperAdminLayout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <style>
        {`
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(30px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .fade-in-up { animation: fadeInUp 0.6s ease-out; }

        `}
      </style>
      
        <header style={{
          background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 50%, #10b981 100%)',
          color: 'white',
          padding: '16px 20px',
          boxShadow: '0 4px 15px rgba(6, 182, 212, 0.2)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1 style={{ fontSize: '1.5rem', fontWeight: '700', margin: '0 0 4px 0' }}>Tenant Management</h1>
              <p style={{ color: 'rgba(255,255,255,0.8)', margin: 0, fontSize: '0.85rem' }}>Manage organizations</p>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.2)',
              padding: '6px 12px',
              borderRadius: '8px',
              fontSize: '0.8rem',
              fontWeight: '600'
            }}>
              Active: {tenants.filter(t => t.subscription?.isActive).length}
            </div>
          </div>
        </header>

      <main style={{ padding: '16px', overflow: 'auto' }}>
        <div style={{ display: 'grid', gap: '16px' }}>
        {/* Compact Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #06b6d4, #10b981)',
            color: 'white',
            padding: '16px',
            borderRadius: '12px',
            textAlign: 'center',
            boxShadow: '0 4px 15px rgba(6, 182, 212, 0.2)'
          }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '4px' }}>🏢</div>
            <div style={{ fontSize: '1.3rem', fontWeight: '700' }}>{tenants.length}</div>
            <div style={{ opacity: 0.9, fontSize: '0.8rem' }}>Total Tenants</div>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #10b981, #14b8a6)',
            color: 'white',
            padding: '16px',
            borderRadius: '12px',
            textAlign: 'center',
            boxShadow: '0 4px 15px rgba(16, 185, 129, 0.2)'
          }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '4px' }}>✅</div>
            <div style={{ fontSize: '1.3rem', fontWeight: '700' }}>
              {tenants.filter(t => t.subscription?.isActive).length}
            </div>
            <div style={{ opacity: 0.9, fontSize: '0.8rem' }}>Active</div>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #0891b2, #06b6d4)',
            color: 'white',
            padding: '16px',
            borderRadius: '12px',
            textAlign: 'center',
            boxShadow: '0 4px 15px rgba(8, 145, 178, 0.2)'
          }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '4px' }}>💰</div>
            <div style={{ fontSize: '1.3rem', fontWeight: '700' }}>
              ₹{tenants.reduce((sum, t) => sum + (t.stats?.revenue || 0), 0).toLocaleString()}
            </div>
            <div style={{ opacity: 0.9, fontSize: '0.8rem' }}>Revenue</div>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #14b8a6, #0891b2)',
            color: 'white',
            padding: '16px',
            borderRadius: '12px',
            textAlign: 'center',
            boxShadow: '0 4px 15px rgba(20, 184, 166, 0.2)'
          }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '4px' }}>🎫</div>
            <div style={{ fontSize: '1.3rem', fontWeight: '700' }}>
              {tenants.reduce((sum, t) => sum + (t.stats?.totalBookings || 0), 0).toLocaleString()}
            </div>
            <div style={{ opacity: 0.9, fontSize: '0.8rem' }}>Bookings</div>
          </div>
        </div>

        {/* Compact Controls */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flex: 1 }}>
            <div style={{ position: 'relative', minWidth: '200px' }}>
              <input
                type="text"
                placeholder="Search tenants..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px 8px 35px',
                  borderRadius: '20px',
                  border: '2px solid rgba(6, 182, 212, 0.2)',
                  background: 'white',
                  color: '#2d3748',
                  fontSize: '13px',
                  outline: 'none'
                }}
              />
              <div style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '14px'
              }}>🔍</div>
            </div>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '10px',
                border: '2px solid rgba(6, 182, 212, 0.2)',
                background: 'white',
                color: '#2d3748',
                fontSize: '13px',
                minWidth: '100px'
              }}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              background: 'linear-gradient(135deg, #06b6d4, #10b981)',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '10px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(6, 182, 212, 0.3)'
            }}
          >
            + Add Tenant
          </button>
        </div>

        {/* Compact Tenants Grid */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '2rem', marginBottom: '12px' }}>⏳</div>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#64748b' }}>Loading tenants...</h3>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '16px'
          }}>
            {filteredTenants.map((tenant, index) => (
              <div key={tenant._id} style={{
                background: 'white',
                borderRadius: '12px',
                padding: '16px',
                boxShadow: '0 4px 15px rgba(6, 182, 212, 0.1)',
                border: '1px solid rgba(6, 182, 212, 0.1)',
                position: 'relative'
              }}>
                {/* Status Badge */}
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  background: tenant.subscription?.isActive 
                    ? 'linear-gradient(135deg, #10b981, #14b8a6)' 
                    : 'linear-gradient(135deg, #ef4444, #dc2626)',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '10px',
                  fontWeight: '600',
                  color: 'white'
                }}>
                  {tenant.subscription?.isActive ? '✅' : '❌'}
                </div>

                {/* Tenant Header */}
                <div style={{ marginBottom: '12px', paddingRight: '40px' }}>
                  <h3 style={{ margin: '0 0 4px', fontSize: '1.1rem', fontWeight: '700', color: '#1e293b' }}>
                    {tenant.name}
                  </h3>
                  <p style={{ margin: 0, color: '#64748b', fontSize: '12px' }}>
                    {tenant.subdomain}.tiketjini.com
                  </p>
                </div>

                {/* Compact Stats */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  gap: '8px',
                  marginBottom: '12px',
                  padding: '10px',
                  background: '#f8fafc',
                  borderRadius: '8px'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1e293b' }}>
                      {tenant.stats?.totalBookings || 0}
                    </div>
                    <div style={{ fontSize: '10px', color: '#64748b' }}>Bookings</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1e293b' }}>
                      ₹{(tenant.stats?.revenue || 0).toLocaleString()}
                    </div>
                    <div style={{ fontSize: '10px', color: '#64748b' }}>Revenue</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1e293b' }}>
                      {tenant.stats?.activeTickets || 0}
                    </div>
                    <div style={{ fontSize: '10px', color: '#64748b' }}>Tickets</div>
                  </div>
                </div>

                {/* Contact Info */}
                <div style={{ marginBottom: '12px', fontSize: '11px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                    <span>📧</span>
                    <span style={{ color: '#64748b' }}>{tenant.contact?.email}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>📅</span>
                    <span style={{ color: '#64748b' }}>
                      {tenant.subscription?.plan?.toUpperCase()} Plan
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button
                    onClick={() => toggleTenantStatus(tenant._id, tenant.subscription?.isActive)}
                    style={{
                      background: tenant.subscription?.isActive 
                        ? 'linear-gradient(135deg, #ef4444, #dc2626)' 
                        : 'linear-gradient(135deg, #10b981, #14b8a6)',
                      color: 'white',
                      border: 'none',
                      padding: '6px 8px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    {tenant.subscription?.isActive ? '❌' : '✅'}
                  </button>
                  <button
                    onClick={() => startEditTenant(tenant)}
                    style={{
                      background: 'linear-gradient(135deg, #06b6d4, #10b981)',
                      color: 'white',
                      border: 'none',
                      padding: '6px 8px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDeleteTenant(tenant._id)}
                    style={{
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      padding: '6px 8px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    🗑️
                  </button>
                  <button
                    onClick={() => setSelectedTenant(tenant)}
                    style={{
                      background: 'white',
                      color: '#06b6d4',
                      border: '1px solid #06b6d4',
                      padding: '6px 8px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    🔍
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {filteredTenants.length === 0 && !loading && (
          <div style={{
            background: 'white',
            padding: '40px',
            textAlign: 'center',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(6, 182, 212, 0.1)',
            border: '1px solid rgba(6, 182, 212, 0.1)'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '12px' }}>🔍</div>
            <h3 style={{ fontSize: '1.1rem', fontWeight: '700', marginBottom: '8px', color: '#1e293b' }}>No Tenants Found</h3>
            <p style={{ color: '#64748b', marginBottom: '16px', fontSize: '0.9rem' }}>Try adjusting your search or filter criteria</p>
            <button
              onClick={() => { setSearchTerm(''); setFilterStatus('all'); }}
              style={{
                background: 'linear-gradient(135deg, #06b6d4, #10b981)',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '600'
              }}
            >
              Clear Filters
            </button>
          </div>
        )}

        {/* Compact Add Tenant Modal */}
        {showAddModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '16px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: window.innerWidth <= 768 ? '16px' : '24px',
              maxWidth: window.innerWidth <= 768 ? '95vw' : '500px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(6, 182, 212, 0.3)',
              border: '1px solid rgba(6, 182, 212, 0.2)',
              position: 'relative'
            }}>
              {/* Compact Header */}
              <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  background: 'linear-gradient(135deg, #06b6d4, #10b981)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 12px',
                  boxShadow: '0 8px 25px rgba(6, 182, 212, 0.3)'
                }}>
                  <span style={{ fontSize: '1.5rem', color: 'white' }}>🏢</span>
                </div>
                <h2 style={{ 
                  margin: 0, 
                  fontSize: '1.5rem', 
                  fontWeight: '700', 
                  color: '#1e293b'
                }}>
                  {editingTenant ? 'Edit Tenant' : 'Add New Tenant'}
                </h2>
                <p style={{ 
                  margin: '4px 0 0', 
                  color: '#64748b', 
                  fontSize: '0.9rem'
                }}>
                  {editingTenant ? 'Update organization details' : 'Create organization account'}
                </p>
              </div>
              
              <form onSubmit={handleAddTenant}>
                <div style={{ display: 'grid', gap: '16px' }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontWeight: '600', 
                      color: '#1e293b',
                      fontSize: '0.9rem'
                    }}>
                      🏢 Organization Name
                    </label>
                    <input
                      type="text"
                      value={newTenant.name}
                      onChange={(e) => setNewTenant({...newTenant, name: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid rgba(6, 182, 212, 0.2)',
                        borderRadius: '8px',
                        fontSize: '14px',
                        color: '#1e293b',
                        outline: 'none',
                        transition: 'all 0.3s ease'
                      }}
                      placeholder="Enter organization name"
                      required
                      onFocus={(e) => e.target.style.borderColor = '#06b6d4'}
                      onBlur={(e) => e.target.style.borderColor = 'rgba(6, 182, 212, 0.2)'}
                    />
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontWeight: '600', 
                      color: '#1e293b',
                      fontSize: '0.9rem'
                    }}>
                      🌐 Subdomain
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        value={newTenant.subdomain}
                        onChange={(e) => setNewTenant({...newTenant, subdomain: e.target.value.toLowerCase()})}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          paddingRight: '120px',
                          border: '2px solid rgba(6, 182, 212, 0.2)',
                          borderRadius: '8px',
                          fontSize: '14px',
                          color: '#1e293b',
                          outline: 'none',
                          transition: 'all 0.3s ease'
                        }}
                        placeholder="wonderland"
                        required
                        onFocus={(e) => e.target.style.borderColor = '#06b6d4'}
                        onBlur={(e) => e.target.style.borderColor = 'rgba(6, 182, 212, 0.2)'}
                      />
                      <div style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#64748b',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: 'rgba(100, 116, 139, 0.1)',
                        padding: '2px 6px',
                        borderRadius: '4px'
                      }}>
                        .tiketjini.com
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '6px', 
                        fontWeight: '600', 
                        color: '#1e293b',
                        fontSize: '0.9rem'
                      }}>
                        📧 Email
                      </label>
                      <input
                        type="email"
                        value={newTenant.contact.email}
                        onChange={(e) => setNewTenant({
                          ...newTenant, 
                          contact: {...newTenant.contact, email: e.target.value}
                        })}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '2px solid rgba(6, 182, 212, 0.2)',
                          borderRadius: '8px',
                          fontSize: '14px',
                          color: '#1e293b',
                          outline: 'none',
                          transition: 'all 0.3s ease'
                        }}
                        placeholder="admin@org.com"
                        required
                        onFocus={(e) => e.target.style.borderColor = '#06b6d4'}
                        onBlur={(e) => e.target.style.borderColor = 'rgba(6, 182, 212, 0.2)'}
                      />
                    </div>

                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '6px', 
                        fontWeight: '600', 
                        color: '#1e293b',
                        fontSize: '0.9rem'
                      }}>
                        🔒 Password
                      </label>
                      <input
                        type="password"
                        value={newTenant.password || ''}
                        onChange={(e) => setNewTenant({
                          ...newTenant, 
                          password: e.target.value
                        })}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '2px solid rgba(6, 182, 212, 0.2)',
                          borderRadius: '8px',
                          fontSize: '14px',
                          color: '#1e293b',
                          outline: 'none',
                          transition: 'all 0.3s ease'
                        }}
                        placeholder={editingTenant ? "Leave blank to keep current password" : "Enter password"}
                        required={!editingTenant}
                        onFocus={(e) => e.target.style.borderColor = '#06b6d4'}
                        onBlur={(e) => e.target.style.borderColor = 'rgba(6, 182, 212, 0.2)'}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '6px', 
                      fontWeight: '600', 
                      color: '#1e293b',
                      fontSize: '0.9rem'
                    }}>
                      📞 Phone
                    </label>
                    <input
                      type="tel"
                      value={newTenant.contact.phone}
                      onChange={(e) => setNewTenant({
                        ...newTenant, 
                        contact: {...newTenant.contact, phone: e.target.value}
                      })}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid rgba(6, 182, 212, 0.2)',
                        borderRadius: '8px',
                        fontSize: '14px',
                        color: '#1e293b',
                        outline: 'none',
                        transition: 'all 0.3s ease'
                      }}
                      placeholder="+91 9876543210"
                      onFocus={(e) => e.target.style.borderColor = '#06b6d4'}
                      onBlur={(e) => e.target.style.borderColor = 'rgba(6, 182, 212, 0.2)'}
                    />
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px', 
                      fontWeight: '600', 
                      color: '#1e293b',
                      fontSize: '0.9rem'
                    }}>
                      📅 Subscription Plan
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr 1fr', gap: '8px' }}>
                      {[
                        { key: 'basic', name: 'Basic', price: '₹999', color: '#06b6d4' },
                        { key: 'premium', name: 'Premium', price: '₹2999', color: '#10b981' },
                        { key: 'enterprise', name: 'Enterprise', price: '₹5999', color: '#0891b2' }
                      ].map(plan => (
                        <div
                          key={plan.key}
                          onClick={() => setNewTenant({
                            ...newTenant,
                            subscription: {...newTenant.subscription, plan: plan.key}
                          })}
                          style={{
                            padding: '10px',
                            border: newTenant.subscription.plan === plan.key ? `2px solid ${plan.color}` : '2px solid #e2e8f0',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            textAlign: 'center',
                            background: newTenant.subscription.plan === plan.key ? `${plan.color}15` : 'white',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <div style={{ fontWeight: '600', color: plan.color, fontSize: '0.8rem' }}>{plan.name}</div>
                          <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#1e293b' }}>{plan.price}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px', 
                      fontWeight: '600', 
                      color: '#1e293b',
                      fontSize: '0.9rem'
                    }}>
                      🎯 Allowed Features
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: '8px' }}>
                      {[
                        { key: 'entry_ticket', label: 'Entry Ticket', icon: '🎫' },
                        { key: 'parking_ticket', label: 'Parking', icon: '🅿️' },
                        { key: 'chappal_stand_ticket', label: 'Chappal Stand', icon: '👟' },
                        { key: 'puja_ticket', label: 'Puja Services', icon: '🙏' },
                        { key: 'photo_shoot_ticket', label: 'Photo Shoots', icon: '📸' },
                        { key: 'function_hall_ticket', label: 'Function Hall', icon: '🎉' },
                        { key: 'event_ticket', label: 'Events', icon: '🎭' },
                        { key: 'pass_ticket', label: 'Pass Tickets', icon: '📅' },
                        { key: 'other', label: 'Other', icon: '🏛️' }
                      ].map(feature => (
                        <label key={feature.key} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px',
                          border: (newTenant.allowedFeatures || []).includes(feature.key) ? '2px solid #06b6d4' : '2px solid #e2e8f0',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          background: (newTenant.allowedFeatures || []).includes(feature.key) ? 'rgba(6, 182, 212, 0.05)' : 'white',
                          transition: 'all 0.3s ease',
                          fontSize: '0.8rem'
                        }}>
                          <input
                            type="checkbox"
                            checked={(newTenant.allowedFeatures || []).includes(feature.key)}
                            onChange={(e) => {
                              const currentFeatures = newTenant.allowedFeatures || [];
                              const updatedFeatures = e.target.checked 
                                ? [...currentFeatures, feature.key]
                                : currentFeatures.filter(f => f !== feature.key);
                              setNewTenant({...newTenant, allowedFeatures: updatedFeatures});
                            }}
                            style={{ accentColor: '#06b6d4' }}
                          />
                          <span>{feature.icon}</span>
                          <span>{feature.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    style={{
                      flex: 1,
                      background: '#f1f5f9',
                      color: '#64748b',
                      border: '1px solid #e2e8f0',
                      padding: '10px 16px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      flex: 2,
                      background: 'linear-gradient(135deg, #06b6d4, #10b981)',
                      color: 'white',
                      border: 'none',
                      padding: '10px 16px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      boxShadow: '0 4px 15px rgba(6, 182, 212, 0.3)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {editingTenant ? '💾 Update Tenant' : '✨ Create Tenant'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Tenant Details Modal */}
        {selectedTenant && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '40px',
              maxWidth: '600px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              maxHeight: '80vh',
              overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h2 style={{ margin: 0, fontSize: '1.8rem', fontWeight: '700', color: '#2d3748' }}>
                  {selectedTenant.name}
                </h2>
                <button
                  onClick={() => setSelectedTenant(null)}
                  style={{
                    background: '#f7fafc',
                    color: '#4a5568',
                    border: 'none',
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    fontSize: '20px'
                  }}
                >
                  ×
                </button>
              </div>

              {/* Detailed Stats */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '16px',
                marginBottom: '24px'
              }}>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  padding: '16px',
                  borderRadius: '12px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>
                    {selectedTenant.stats?.totalBookings || 0}
                  </div>
                  <div style={{ fontSize: '12px', opacity: 0.8 }}>Total Bookings</div>
                </div>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  padding: '16px',
                  borderRadius: '12px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>
                    ₹{(selectedTenant.stats?.revenue || 0).toLocaleString()}
                  </div>
                  <div style={{ fontSize: '12px', opacity: 0.8 }}>Revenue</div>
                </div>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  padding: '16px',
                  borderRadius: '12px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>
                    {selectedTenant.stats?.activeTickets || 0}
                  </div>
                  <div style={{ fontSize: '12px', opacity: 0.8 }}>Active Tickets</div>
                </div>
              </div>

              {/* Tenant Information */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 16px', fontSize: '1.2rem', fontWeight: '600' }}>Tenant Information</h3>
                <div style={{ display: 'grid', gap: '12px', fontSize: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ opacity: 0.8 }}>Subdomain:</span>
                    <span style={{ fontWeight: '600' }}>{selectedTenant.subdomain}.tiketjini.com</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ opacity: 0.8 }}>Email:</span>
                    <span style={{ fontWeight: '600' }}>{selectedTenant.contact?.email}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ opacity: 0.8 }}>Phone:</span>
                    <span style={{ fontWeight: '600' }}>{selectedTenant.contact?.phone}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ opacity: 0.8 }}>Plan:</span>
                    <span style={{ fontWeight: '600', textTransform: 'uppercase' }}>
                      {selectedTenant.subscription?.plan}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ opacity: 0.8 }}>Status:</span>
                    <span style={{
                      fontWeight: '600',
                      color: selectedTenant.subscription?.isActive ? '#48bb78' : '#e53e3e'
                    }}>
                      {selectedTenant.subscription?.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ opacity: 0.8 }}>Created:</span>
                    <span style={{ fontWeight: '600' }}>
                      {new Date(selectedTenant.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => toggleTenantStatus(selectedTenant._id, selectedTenant.subscription?.isActive)}
                  style={{
                    background: selectedTenant.subscription?.isActive 
                      ? 'linear-gradient(45deg, #e53e3e, #c53030)' 
                      : 'linear-gradient(45deg, #48bb78, #38a169)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 20px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {selectedTenant.subscription?.isActive ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  style={{
                    background: 'linear-gradient(45deg, #667eea, #764ba2)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 20px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  View Analytics
                </button>
                <button
                  style={{
                    background: 'linear-gradient(45deg, #ff6b6b, #feca57)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 20px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Edit Tenant
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </main>
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminTenants;